import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getResume, updateResume } from "../services/resumeService";
import ResumeForm from "../components/ResumeForm";
import ResumePreview from "../components/ResumePreview";
import TemplatePicker from "../components/TemplatePicker";
import AIPanel from "../components/AIPanel";
import { FiSave, FiDownload, FiArrowLeft, FiEye, FiEdit, FiLoader, FiChevronDown, FiX } from "react-icons/fi";
import { DEFAULT_TEMPLATE_ID, getDefaultAccentForTemplate } from "../components/templates/templateRegistry";

const LEGACY_TEMPLATE_MAP = {
  professional: "popular-classic-two-column",
  modern: "modern-teal-header",
  creative: "creative-sidebar-shapes",
};

export default function ResumeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [view, setView] = useState(window.innerWidth < 768 ? "form" : "split"); // form-only on mobile
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE_ID);
  const [accentColor, setAccentColor] = useState(getDefaultAccentForTemplate(DEFAULT_TEMPLATE_ID));
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);
  const [importToast, setImportToast] = useState("");

  useEffect(() => {
    if (location.state?.importedToast) {
      setImportToast(location.state.importedToast);
      const timer = window.setTimeout(() => setImportToast(""), 3000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [location.state]);

  useEffect(() => {
    getResume(id)
      .then((data) => {
        setResume(data);
        const resolvedTemplate = LEGACY_TEMPLATE_MAP[data.template] || data.template || DEFAULT_TEMPLATE_ID;
        setTemplate(resolvedTemplate);
        setAccentColor(data.accent_color || getDefaultAccentForTemplate(resolvedTemplate));
      })
      .catch(() => navigate("/dashboard"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSave = useCallback(async () => {
    if (!resume) return;
    setSaving(true);
    try {
      await updateResume(id, { ...resume, template, accent_color: accentColor });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert("Failed to save resume.");
    } finally {
      setSaving(false);
    }
  }, [id, resume, template, accentColor]);

  const handleDownloadPDF = async () => {
    const el = document.getElementById("resume-preview");
    if (!el) return alert("Preview not visible. Switch to preview mode first.");

    const html2pdf = (await import("html2pdf.js")).default;
    html2pdf()
      .set({
        margin: 0,
        filename: `${resume?.title || "Resume"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      })
      .from(el)
      .save();
  };

  const handleChange = (updated) => {
    setResume(updated);
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!resume) return null;

  return (
    <div className="min-h-screen bg-slate-50 animate-fade-in">
      {importToast && (
        <div className="fixed right-4 top-20 z-[60] no-print">
          <div className="rounded-lg px-4 py-3 shadow-lg border bg-emerald-50 border-emerald-200 text-emerald-700">
            <p className="text-sm font-medium">{importToast}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40 no-print">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-slate-500 hover:text-slate-700 transition">
              <FiArrowLeft className="text-lg" />
            </button>
            <input
              type="text"
              className="text-lg font-semibold text-slate-800 border-none focus:ring-0 bg-transparent w-48 sm:w-64"
              value={resume.title || ""}
              onChange={(e) => handleChange({ ...resume, title: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap md:flex-nowrap justify-end">
            {/* View toggles */}
            <div className="flex border border-slate-200 rounded-lg overflow-hidden shrink-0">
              <ViewBtn active={view === "form"} onClick={() => setView("form")}><FiEdit /></ViewBtn>
              <ViewBtn active={view === "split"} onClick={() => setView("split")} className="hidden md:block">Split</ViewBtn>
              <ViewBtn active={view === "preview"} onClick={() => setView("preview")}><FiEye /></ViewBtn>
            </div>

            <button
              type="button"
              onClick={() => setIsTemplatePickerOpen(true)}
              className="flex items-center gap-1 bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-900 transition shrink-0 whitespace-nowrap"
            >
              Templates
              <FiChevronDown />
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                saved
                  ? "bg-green-100 text-green-700"
                  : "bg-primary-600 text-white hover:bg-primary-700"
              } disabled:opacity-50 shrink-0 whitespace-nowrap`}
            >
              {saving ? <FiLoader className="animate-spin" /> : <FiSave />}
              {saved ? "Saved!" : "Save"}
            </button>

            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-1 bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-900 transition shrink-0 whitespace-nowrap"
            >
              <FiDownload /> PDF
            </button>
          </div>
        </div>
      </div>

      {/* Editor content */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <div className={`grid gap-6 grid-cols-1 ${view === "split" ? "md:[grid-template-columns:1fr_1fr]" : ""}`}>
          {/* Left – Form + AI */}
          {(view === "split" || view === "form") && (
            <div className="space-y-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 180px)" }}>
              <AIPanel resume={resume} onUpdate={handleChange} />
              <ResumeForm resume={resume} onChange={handleChange} />
            </div>
          )}

          {/* Right – Preview */}
          {(view === "split" || view === "preview") && (
            <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 180px)" }}>
              <ResumePreview resume={resume} template={template} accentColor={accentColor} />
            </div>
          )}
        </div>
      </div>

      {isTemplatePickerOpen && (
        <div className="fixed inset-0 z-50 no-print">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/60"
            aria-label="Close template gallery"
            onClick={() => setIsTemplatePickerOpen(false)}
          />
          <div className="relative w-full h-full flex items-end md:items-center justify-center md:p-4">
            <div className="bg-white w-full h-[88vh] rounded-t-2xl overflow-hidden flex flex-col animate-drawer-up md:animate-none md:h-auto md:w-full md:max-w-[900px] md:max-h-[85vh] md:rounded-xl md:shadow-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                <h2 className="text-base sm:text-lg font-semibold text-slate-800">Template Gallery</h2>
                <button
                  type="button"
                  onClick={() => setIsTemplatePickerOpen(false)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  aria-label="Close template gallery"
                >
                  <FiX className="text-lg" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <TemplatePicker
                  selectedTemplate={template}
                  selectedAccent={accentColor}
                  onChooseTemplate={({ template: nextTemplate, accentColor: nextAccentColor }) => {
                    setTemplate(nextTemplate);
                    setAccentColor(nextAccentColor || getDefaultAccentForTemplate(nextTemplate));
                    setSaved(false);
                    setIsTemplatePickerOpen(false);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ViewBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium transition ${
        active ? "bg-primary-600 text-white" : "text-slate-500 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}
